import { type ReactNode } from 'react'
import { observer } from 'mobx-react-lite'
import { makeTextFieldIcon } from 'shared/ui/TextField/ActionsAside/TextFieldIcon'
import { Dropdown, DropdownContent, DropdownGroup, type IDropdownItem } from 'shared/ui/Dropdown'
import { TextFieldPlaceholder } from 'shared/ui/TextField/TextFieldPlaceholder'
import { SpinnerLoader } from 'shared/ui/Loader/ui/SpinnerLoader'
import { Error } from 'shared/ui'
import { SelectValueTrigger } from './SelectValueTrigger'
import styles from './styles.module.scss'

type ISelectValueDropdownProps = {
  isValueSelected: boolean
  isLoading: boolean
  itemsList?: IDropdownItem[]
  setSelectedValue?: (item: IDropdownItem) => void
  errorMessage: string | undefined
  removeError?: () => void
  ariaLabel: string
  placeholder: string
  triggerContent?: ReactNode
  selectedItem?: IDropdownItem | null
  customComponent?: (onClose: () => void) => ReactNode
}

export const SelectValueDropdown = observer(
  ({
    isValueSelected,
    isLoading,
    itemsList,
    setSelectedValue,
    errorMessage,
    removeError,
    ariaLabel,
    placeholder,
    triggerContent,
    selectedItem,
    customComponent,
  }: ISelectValueDropdownProps) => {
    return (
      <>
        <Dropdown
          ariaLabel={ariaLabel}
          width={460}
          triggerComponent={() => (
            <TextFieldPlaceholder
              multiline
              variant='stroke'
              size='medium'
              rightActions={[makeTextFieldIcon({ icon: 'chevronDown' })]}
              className={styles.selectLabel}
              error={errorMessage}
              placeholder={
                <SelectValueTrigger
                  isLoading={isLoading}
                  isValueSelected={isValueSelected}
                  placeholder={placeholder}
                  triggerContent={triggerContent}
                  selectedItem={selectedItem}
                />
              }
            />
          )}
          customComponent={(onClose) => (
            <>
              {isLoading ? (
                <DropdownContent>
                  <div className={styles.dropdownLoader}>
                    <SpinnerLoader ariaLabel={'SelectValueDropdown'} size={20} />
                  </div>
                </DropdownContent>
              ) : (
                <DropdownContent noPadding>
                  {customComponent?.(onClose) || (
                    <DropdownGroup
                      items={itemsList}
                      isScroll={false}
                      onChange={(item) => {
                        setSelectedValue?.(item)
                        removeError?.()
                        onClose()
                      }}
                      stylesWrap={{
                        padding: '4px',
                      }}
                    />
                  )}
                </DropdownContent>
              )}
            </>
          )}
          placement={'bottom'}
          margin={0}
          PopperProps={{
            modifiers: [
              {
                name: 'offset',
                options: {
                  offset: () => {
                    return [0, -40]
                  },
                },
              },
            ],
          }}
        />
        <Error type={'text'} error={errorMessage} />
      </>
    )
  }
)
